import { useCallback, useEffect, useState } from "react";
import { useLocation } from "wouter";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  type CockpitDashboardView,
  getCockpitDashboard,
} from "@/lib/api";
import {
  type ApiErrorDebug,
  classifyApiError,
  formatApiDebug,
} from "@/hooks/useApiErrorState";
import { DeadlineProgressBar } from "./DeadlineProgressBar";
import { InstantInsightPanel } from "./InstantInsightPanel";
import { NextBestActionCard } from "./NextBestActionCard";
import { PrioritizedCaseList } from "./PrioritizedCaseList";

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

interface CockpitError {
  message: string;
  hint?: string;
  debug?: ApiErrorDebug;
}

function describeError(err: unknown): CockpitError {
  // Log brut pour diagnostic via la console developpeur : permet de
  // distinguer un timeout, un CORS deny, une 401 et un 5xx sans avoir
  // a redeployer pour ajouter des logs.
  // eslint-disable-next-line no-console
  console.error("[Cockpit] dashboard fetch failed:", err);

  const c = classifyApiError(err);
  const debug = "debug" in c ? c.debug : undefined;

  if (c.kind === "art52") {
    return {
      message: c.detail,
      hint: "Profil arbitre en attente de validation humaine (AI Act Art. 52). Un administrateur doit valider votre compte.",
      debug,
    };
  }
  if (c.kind === "unauthenticated") {
    return {
      message: c.detail,
      hint: "Votre session a peut-etre expire. Deconnectez-vous puis reconnectez-vous.",
      debug,
    };
  }
  if (c.kind === "forbidden") {
    return { message: c.detail, debug };
  }
  if (c.kind === "network") {
    let hint =
      "Verifiez votre connexion (le VPN peut bloquer l'acces a l'API).";
    if (debug?.code === "ECONNABORTED") {
      hint =
        "Le serveur a mis trop de temps a repondre (cold-start ou agregation lente). Reessayez dans quelques secondes.";
    } else if (
      debug?.code === "ERR_NETWORK" ||
      debug?.message?.toLowerCase().includes("network")
    ) {
      hint =
        "La requete a echoue avant d'atteindre le serveur. Causes typiques : preflight CORS bloque par le navigateur (Safari ITP / extension), VPN/DNS qui filtre, ou bug navigateur sur les requetes cross-origin avec entete Authorization.";
    }
    return { message: c.detail, hint, debug };
  }
  if (c.kind === "unavailable") {
    return {
      message: c.detail,
      hint: "Service IA indisponible temporairement. Reessayez dans quelques minutes.",
      debug,
    };
  }
  if (c.kind !== "none") return { message: c.detail, debug };
  return {
    message: "Cockpit indisponible. Reessayez plus tard.",
  };
}

export function CockpitDashboard() {
  const [, setLocation] = useLocation();
  const [dashboard, setDashboard] = useState<CockpitDashboardView | null>(null);
  const [error, setError] = useState<CockpitError | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await getCockpitDashboard();
      setDashboard(data);
      setError(null);
      setSelectedCaseId((prev) => {
        if (prev !== null || data.cases.length === 0) return prev;
        const firstUrgent =
          data.cases.find(
            (c) => c.days_remaining !== null && c.days_remaining <= 14,
          ) ?? data.cases[0];
        return firstUrgent.id;
      });
    } catch (err) {
      setError(describeError(err));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      await load();
    };
    void tick();
    const interval = setInterval(() => void tick(), REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [load]);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await load();
    } finally {
      setRetrying(false);
    }
  };

  if (error) {
    return (
      <div
        className="rounded-md border p-6 text-sm max-w-2xl space-y-3"
        style={{
          borderColor: "color-mix(in oklch, var(--block-red) 40%, transparent)",
          background: "color-mix(in oklch, var(--block-red) 10%, transparent)",
          color: "rgb(254 202 202)",
        }}
      >
        <div>
          <strong className="block mb-1">
            Chargement du cockpit impossible
          </strong>
          <p>{error.message}</p>
          {error.hint && (
            <p
              className="mt-2 text-xs"
              style={{ color: "rgb(254 226 226)" }}
            >
              {error.hint}
            </p>
          )}
          {error.debug && (
            <details className="mt-3">
              <summary
                className="text-[11px] cursor-pointer"
                style={{ color: "rgb(254 202 202)" }}
              >
                Details techniques
              </summary>
              <pre
                className="mt-1.5 text-[10px] whitespace-pre-wrap break-all gsf-mono-proof rounded-md p-2"
                style={{
                  background: "var(--graphite-900)",
                  color: "var(--arbitre-slate-300)",
                  border: "1px solid var(--graphite-700)",
                }}
              >
                {formatApiDebug(error.debug)}
              </pre>
            </details>
          )}
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => void handleRetry()}
          disabled={retrying}
        >
          {retrying ? (
            <Loader2 className="animate-spin mr-1.5" size={14} />
          ) : (
            <RefreshCw size={14} className="mr-1.5" />
          )}
          Reessayer
        </Button>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div
        className="flex items-center justify-center gap-2 py-16 text-sm"
        style={{ color: "var(--arbitre-slate-300)" }}
      >
        <Loader2 className="animate-spin" size={20} />
        Chargement du cockpit…
      </div>
    );
  }

  const stats = dashboard.stats;

  return (
    <div className="space-y-6">
      {/* Header */}
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div
            className="flex items-center gap-2 text-caption-arbitre"
            style={{ color: "var(--proof-gold)" }}
          >
            <Sparkles size={12} />
            Maison numerique de l'arbitre
          </div>
          <h1 className="text-h1-arbitre mt-1">Cockpit</h1>
          <p
            className="text-body-arbitre mt-1"
            style={{ color: "var(--arbitre-slate-300)" }}
          >
            Bienvenue, {dashboard.arbiter_name}.
          </p>
        </div>
        <div className="flex gap-3">
          <StatBadge
            label="Actifs"
            value={stats.active_cases}
            color="var(--proof-gold)"
            icon={<Clock size={12} />}
          />
          <StatBadge
            label="Urgents"
            value={stats.urgent_cases}
            color="var(--block-red)"
            icon={<AlertTriangle size={12} />}
          />
          <StatBadge
            label="Termines"
            value={stats.completed_cases}
            color="var(--arbitre-slate-300)"
            icon={<CheckCircle2 size={12} />}
          />
        </div>
      </header>

      {dashboard.global_insight && (
        <div
          className="rounded-md border p-3 text-sm flex items-start gap-2"
          style={{
            borderColor:
              "color-mix(in oklch, var(--ai-violet) 35%, transparent)",
            background:
              "color-mix(in oklch, var(--ai-violet) 10%, transparent)",
            color: "var(--arbitre-slate-100)",
          }}
        >
          <Sparkles
            size={14}
            className="mt-0.5 shrink-0"
            style={{ color: "var(--ai-violet)" }}
          />
          <span>{dashboard.global_insight}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Colonne principale */}
        <div className="lg:col-span-2 space-y-5">
          {dashboard.next_best_actions.length > 0 && (
            <section className="gsf-surface p-4">
              <h2
                className="text-h3-arbitre mb-3 flex items-center gap-2"
                style={{ color: "var(--arbitre-slate-100)" }}
              >
                <Sparkles size={14} style={{ color: "var(--ai-violet)" }} />
                Actions recommandees
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {dashboard.next_best_actions.slice(0, 4).map((a) => (
                  <NextBestActionCard
                    key={a.id}
                    action={a}
                    onClick={() => {
                      setSelectedCaseId(a.case_id);
                      // Navigation directe si l'action porte sur un
                      // type "validate_sentence" ou "review_document"
                      // — mais on garde une UX prudente : on selectionne
                      // d'abord le dossier dans l'insight, et l'arbitre
                      // confirme ensuite l'action.
                    }}
                  />
                ))}
              </div>
            </section>
          )}

          <section className="gsf-surface p-4">
            <h2
              className="text-h3-arbitre mb-3"
              style={{ color: "var(--arbitre-slate-100)" }}
            >
              Mes dossiers
            </h2>
            <PrioritizedCaseList
              cases={dashboard.cases}
              selectedCaseId={selectedCaseId}
              onSelect={setSelectedCaseId}
            />
          </section>
        </div>

        {/* Colonne droite */}
        <aside className="space-y-5">
          {selectedCaseId !== null && (
            <section className="gsf-surface p-4">
              <h2
                className="text-h3-arbitre mb-3 flex items-center gap-2"
                style={{ color: "var(--arbitre-slate-100)" }}
              >
                <Sparkles size={14} style={{ color: "var(--ai-violet)" }} />
                Instant Insight
              </h2>
              <InstantInsightPanel caseId={selectedCaseId} />
              <button
                type="button"
                onClick={() => setLocation(`/cases/${selectedCaseId}`)}
                className="mt-3 text-xs underline"
                style={{ color: "var(--proof-gold)" }}
              >
                Ouvrir le dossier complet →
              </button>
            </section>
          )}

          {dashboard.stats.active_cases > 0 && (
            <section className="gsf-surface p-4">
              <h2
                className="text-h3-arbitre mb-3"
                style={{ color: "var(--arbitre-slate-100)" }}
              >
                Repartition des echeances
              </h2>
              <DeadlineProgressBar cases={dashboard.cases} />
            </section>
          )}

          {dashboard.notifications.length > 0 && (
            <section className="gsf-surface p-4">
              <h2
                className="text-h3-arbitre mb-3"
                style={{ color: "var(--arbitre-slate-100)" }}
              >
                Notifications ({dashboard.notifications.length})
              </h2>
              <ul className="space-y-2">
                {dashboard.notifications.slice(0, 5).map((n) => (
                  <li
                    key={n.id}
                    className="rounded-md p-2.5"
                    style={{
                      background: "var(--graphite-700)",
                      borderLeft: `2px solid ${
                        n.is_read
                          ? "var(--arbitre-slate-500)"
                          : "var(--proof-gold)"
                      }`,
                    }}
                  >
                    <div
                      className="text-sm font-semibold"
                      style={{ color: "var(--arbitre-slate-100)" }}
                    >
                      {n.title}
                    </div>
                    <p
                      className="text-xs mt-0.5"
                      style={{ color: "var(--arbitre-slate-300)" }}
                    >
                      {n.message}
                    </p>
                    <div
                      className="flex items-center justify-between mt-1 text-[10px]"
                      style={{ color: "var(--arbitre-slate-500)" }}
                    >
                      {n.reference_dossier && (
                        <span className="gsf-mono-proof">
                          {n.reference_dossier}
                        </span>
                      )}
                      <span>
                        {new Date(n.created_at).toLocaleDateString("fr-FR")}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </aside>
      </div>

      <footer
        className="text-[11px] text-center"
        style={{ color: "var(--arbitre-slate-500)" }}
      >
        Donnees mises a jour le{" "}
        {new Date(dashboard.generated_at).toLocaleString("fr-FR")} — rafraichies
        toutes les 5 min.
      </footer>
    </div>
  );
}

function StatBadge({
  label,
  value,
  color,
  icon,
}: {
  label: string;
  value: number;
  color: string;
  icon: React.ReactNode;
}) {
  return (
    <div
      className="gsf-surface px-3 py-2 text-center min-w-[80px]"
      style={{ borderLeft: `2px solid ${color}` }}
    >
      <div
        className="flex items-center justify-center gap-1 text-caption-arbitre"
        style={{ color: "var(--arbitre-slate-500)" }}
      >
        <span style={{ color }}>{icon}</span>
        {label}
      </div>
      <div className="text-2xl font-bold" style={{ color }}>
        {value}
      </div>
    </div>
  );
}
